import api from '@/services/utils/apiInstance.ts';
import { buildQuery } from '@/services/utils/request.ts';

import type { PaginatedQuery } from '@/types/services/utils/request.type.ts';
import type { PaginatedResponse } from '@/types/services/utils/response.type.ts';
import type {
  AdminExpenseManagement,
  AssetRow,
  CreateMandateRequest,
  MandateDocument,
  MandateFeesResponse,
  MandateInvestmentLimit,
  MandateInvestmentResponse,
  MandateList,
  MandateListResponse,
  MandatePublicStatusKey,
  MandateResponse,
  UpdateMandateRequest,
  MandateResponseApi,
  UpdateAdminExpenseManagementRequest
} from '@/types/services/mandate/mandate.type.ts';

function mapMandateResponse(data: MandateResponseApi): MandateResponse {
  const assets: AssetRow[] = data.assets.map((asset: AssetRow) => {
    const percentage = typeof asset.percentage === 'string'
      ? Number(asset.percentage)
      : asset.percentage

    return {
      id: asset.id,
      name: asset.name,
      percentage: Number((Number.isFinite(percentage) ? percentage : 0).toFixed(2))
    }
  })

  const fees: MandateFeesResponse = {
    subscriptionFee: data.fees.subscription_fee ?? null,
    managementFee: data.fees.management_fee ?? null,
    managementPeriodicity: data.fees.management_periodicity ?? null,
    exitFee: data.fees.exit_fee ?? null,
    earlyExitPenalty: data.fees.early_exit_penalty ?? null,
    earlyExitMinDuration: data.fees.early_exit_min_duration ?? null,
    performanceFee: data.fees.performance_fee ?? null,
    accountKeepingFee: data.fees.account_keeping_fee ?? null
  }

  return {
    id: data.id,
    author: data.author,
    authorId: data.author_id,
    name: data.name,
    description: data.description,
    assets: assets,
    risk: data.risk,
    currency: data.currency,
    minimumInvestment: (data.minimum_investment ?? 0),
    maximumInvestment: (data.maximum_investment ?? 0),
    recommendedDuration: data.recommended_duration,
    strategyOperation: data.strategy_operation,
    presentation: data.presentation,
    availableAt: data.available_at,
    closedAt: data.closed_at,
    documents: data.documents,
    youtubeUrl: data.youtube_url,
    publicStatus: data.public_status,
    boStatus: data.bo_status,
    fees: fees,
    custody: data.custody
  };
}

class MandateService {
  async getPaginatedMandate(params: PaginatedQuery): Promise<PaginatedResponse<MandateList>> {
    const query = buildQuery(params)
    const response = await api.get(`/mandate/paginated?${query.toString()}`)
    return {
      total: response.data.total,
      page: response.data.page,
      limit: response.data.limit,
      maxPages: response.data.max_pages,
      data: response.data.data.map((mandate: MandateListResponse) => ({
        id: mandate.id,
        author: mandate.author,
        name: mandate.name,
        publicStatus: mandate.public_status,
        boStatus: mandate.bo_status,
        createdAt: mandate.created_at,
        availableAt: mandate.available_at,
        totalAmountInvested: mandate.total_amount_invested,
        appliedFees: mandate.applied_fees
      }))
    }
  }

  async getMandate(mandateId: number): Promise<MandateResponse> {
    const response = await api.get(`/mandate/${mandateId}`)
    return mapMandateResponse(response.data)
  }

  async getMandateInvestment(mandateId: number, limit: number, page: number): Promise<MandateInvestmentResponse> {
    const response = await api.get(`/mandate/${mandateId}/investment?limit=${limit}&page=${page}`)
    return {
      total: response.data.total,
      userInvestments: response.data.user_investments,
      totalInvestment: response.data.total_investment,
      maxPages: response.data.max_pages,
      limit: response.data.limit,
      page: response.data.page,
    }
  }

  async createMandate(mandateData: CreateMandateRequest): Promise<MandateResponse> {
    const response = await api.post('/mandate', mandateData)
    return mapMandateResponse(response.data)
  }

  async updateMandate(mandateData: UpdateMandateRequest, dicUserNotification: boolean = false): Promise<MandateResponse> {
    const response = await api.put('/mandate/' + mandateData.id, {
      ...mandateData,
      dicUserNotification: dicUserNotification
    })
    return mapMandateResponse(response.data)
  }

  async refuseMandate(mandateId: number, reason: string): Promise<void> {
    await api.post('/mandate/refuse', {
        mandateId: mandateId,
        reasonForRefusal: reason
    })
  }

  async updateStatus(mandateId: number, status: MandatePublicStatusKey): Promise<void> {
    await api.patch('/mandate/' + mandateId + '/' + status )
  }

  async uploadDocument(mandateId: number, documents: Array<MandateDocument>) {
    const formData = new FormData()
    const metas: Array<{id: number | null, name: string, category: string, reportPeriod?: string}> = []

    documents.forEach((document) => {
      formData.append('files', document.file)
      const meta: {id: number | null, name: string, category: string, reportPeriod?: string} = {
        id: document.id,
        name: document.name,
        category: document.category
      }
      if (document.reportPeriod) meta.reportPeriod = document.reportPeriod
      metas.push(meta)
    })

    formData.append('metas', JSON.stringify(metas))

    const response = await api.post(`/mandate/${mandateId}/document`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })

    return response.data
  }

  async deleteDocument(mandateId: number, documentId: number) {
    await api.delete(`/mandate/${mandateId}/document/${documentId}`)
  }

  async downloadDocument(mandateId: number, documentId: number): Promise<string> {
    const reponse = await api.get(`/mandate/${mandateId}/document/${documentId}`)
    return reponse.data.url
  }

  async getMandateInvestmentLimit(): Promise<MandateInvestmentLimit> {
    const response = await api.get(`/mandate/investment-limit`)
    return {
      minInvestment: response.data.min_investment,
      maxInvestment: response.data.max_investment
    }
  }

  async getAdminExpenseManagement(mandateId: number): Promise<AdminExpenseManagement> {
    const response = await api.get(`/mandate/${mandateId}/admin-expense-management`)
    return {
      managementFee: response.data.management_fee,
      managementPeriodicity: response.data.management_periodicity,
      accountKeepingFee: response.data.account_keeping_fee ?? null,
    }
  }

  async updateAdminExpenseManagement(mandateId: number, adminExpenseManagement: UpdateAdminExpenseManagementRequest): Promise<void> {
    // Undefined fields are dropped by JSON serialization, so only what is set is sent.
    await api.put(`/mandate/${mandateId}/admin-expense-management`, {
      managementFee: adminExpenseManagement.managementFee,
      managementPeriodicity: adminExpenseManagement.managementPeriodicity,
      accountKeepingFee: adminExpenseManagement.accountKeepingFee
    });
  }
}

export default new MandateService();
